import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { productsForNewRoom, roomUseCaseOfProduct } from './objectProduct';
import { type UseCase, liveUseCasesWhere, spaceOf } from '../../utils/useCases';
import { useTenantProductCategoriesQuery } from '../../hooks/useTenantProductCategoriesQuery';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Tooltip } from '@mui/material';
import { RootState } from '../../store/store';
import PageHeader from '../../components/PageHeader';
import PageHeadline from '../../components/PageHeadline';
import CustomButton from '../../components/CustomButton';
import { makeAssetDetailsUrl, paths } from '../../routes';
import { useCreateAssetWithAddressMutation } from '../../api/asset/assetApiSlice';
import { useGetAssetsAvailabilityDataQuery } from '../../api/booking/bookingApiSlice';
import {
    AccommodationDetail,
    AccommodationDetailAmenity,
    AccommodationDetailPolicy,
    CreateAssetWithAddressRequest,
} from '../../api/asset/assetApiTypes';
import { useGetProductsQuery } from '../../api/group/groupAdminApiSlice';
import {
    invalidateInventoryReaders,
    useCreateStationMutation,
    useGetHousesQuery,
} from '../../api/inventory/inventoryApiSlice';
import { type AddressConflict, addressConflictMode, addressConflictOf } from './addressConflict';
import InputField from '../../components/ui/InputField';
import CustomModal from '../common/CustomModal';
import RoomPlacementSection, { type RoomPlacement } from './components/RoomPlacementSection';
import SelectField from '../../components/ui/SelectField';
import { FormService } from './AssetDetailPage';
import BasicInfoSection from './components/BasicInfoSection';
import AccommodationSection from './components/AccommodationSection';
import AdditionalServicesSection from './components/AdditionalServicesSection';
import PoliciesSection from './components/PoliciesSection';
import AddressSection, { AssetAddress } from './components/AddressSection';
import CreateImagesSection from './components/CreateImagesSection';
import OpeningHoursSection from './components/OpeningHoursSection';
import AssetDetailSection from './components/AssetDetailSection';
import { OpeningHourEntry } from '../../types/locations';
import { useDefaultTaxCategoryId } from '../../components/tax/useDefaultTaxCategoryId';
import { coordinatesState } from '../../utils/coordinates';
import { objectLimitMessage } from '../../utils/objectLimitMessage';

interface CreateFormState {
    useCase: UseCase | null;
    name: string;
    designation: string;
    address: AssetAddress;
    accomodationdetail: AccommodationDetail;
    additionalServices: FormService[];
    /**
     * D12 (PO 2026-09-28): the room product this object is created into.
     * Prices and long-stay discounts live on the product — the create form
     * asks for neither.
     */
    productId: string;
    policies: AccommodationDetailPolicy[];
    featuredImage: File | null;
    additionalImages: File[];
    openingHours: OpeningHourEntry[];
}

const makeInitialAddress = (): AssetAddress => ({
    street: '',
    houseNumber: '',
    floor: '',
    postalCode: '',
    city: '',
    district: '',
    geoLat: null,
    geoLng: null,
});

// Compose an object name from the address fields per the Neuland convention.
// The pipe separator is load-bearing: extractFloorFromName parses names of
// the form "<head>, <floor> | <tail>" to recover the floor later, and
// extractAddress (booking screens) shows the segment before the first comma.
// District takes precedence after the pipe; city is the fallback so the
// name always carries a location context even when district is skipped.
//
// A designation ("Stadthalle", "Schloss") SUBSTITUTES the street part rather
// than being appended: that keeps both parsers working untouched while giving
// venues the name people actually use for them. Booking screens then render
// "Stadthalle, <street>, <postal code> <city>" — the street is still shown,
// from the address fields, so nothing is lost by leaving it out of the name.
const composeName = (address: AssetAddress, designation: string): string => {
    const streetPart = [address.street, address.houseNumber]
        .map((s) => s.trim())
        .filter(Boolean)
        .join(' ');
    const head0 = designation.trim() || streetPart;
    const floor = address.floor.trim();
    const district = address.district.trim();
    const city = address.city.trim();
    const parts: string[] = [];
    if (head0) parts.push(head0);
    if (floor) parts.push(floor);
    if (parts.length === 0) return '';
    const head = parts.join(', ');
    const tail = district || city;
    return tail ? `${head} | ${tail}` : head;
};

const makeInitialFormState = (): CreateFormState => ({
    useCase: null,
    name: '',
    designation: '',
    address: makeInitialAddress(),
    additionalServices: [],
    accomodationdetail: {
        guests: undefined,
        beds: undefined,
        baths: undefined,
        rooms: undefined,
        kitchen: undefined,
        outdoor: '',
        amenities: [],
        facilityDescriptions: { de: '', en: '' },
        importantNotes: { de: '', en: '' },
        policies: [],
    },
    productId: '',
    policies: [],
    featuredImage: null,
    additionalImages: [],
    openingHours: [],
});

/** What a new address still lacks, or null. */
const newAddressError = (address: AssetAddress, t: (key: string) => string): string | null => {
    if (!address.street.trim()) return t('asset_create.validation.street_required');
    if (!address.postalCode.trim()) return t('asset_create.validation.postal_code_required');
    if (!address.city.trim()) return t('asset_create.validation.city_required');
    // Optional (PO, 2026-10-16) — but a half pin is no place.
    if (coordinatesState(address) === 'incomplete') {
        return t('coordinates.incomplete');
    }
    return null;
};

/** Where the room goes: into a house, or to the form's address (a new location). */
const placementFieldsOf = (
    form: CreateFormState, houseId: string | null, confirmNewLocation: boolean,
): Pick<CreateAssetWithAddressRequest, 'depotId' | 'address' | 'confirmNewLocation'> => {
    if (houseId) return { depotId: houseId };
    return {
        address: {
            street: form.address.street.trim(),
            houseNumber: form.address.houseNumber.trim(),
            postalCode: form.address.postalCode.trim(),
            city: form.address.city.trim(),
            district: form.address.district.trim(),
            geoLat: form.address.geoLat,
            geoLng: form.address.geoLng,
        },
        ...(confirmNewLocation ? { confirmNewLocation: true } : {}),
    };
};

/** The room's own opening hours, when it has a location of its own and shows them. */
const ownOpeningHoursOf = (form: CreateFormState, showsOpeningHours: boolean) => (
    showsOpeningHours && form.openingHours.length > 0 ? form.openingHours : undefined
);

const AssetCreatePage: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [form, setForm] = useState<CreateFormState>(() => makeInitialFormState());
    const [error, setError] = useState<string | null>(null);
    const [openingHoursValid, setOpeningHoursValid] = useState(true);
    // Auto-fill the Name field from address + floor until the user types
    // something in it. Ref (not state) because we don't need to re-render
    // when the touched flag flips.
    const nameTouchedRef = useRef(false);

    const [createAssetWithAddress, { isLoading: saving }] = useCreateAssetWithAddressMutation();

    // Where the room stands (plan room-houses-and-combining-rooms §4 row 2).
    // `?houseId=` arrives from a house's page ("Raum in diesem Haus anlegen").
    const [searchParams] = useSearchParams();
    const [placement, setPlacement] = useState<RoomPlacement>(
        searchParams.get('houseId') ? 'house' : 'new',
    );
    const [houseId, setHouseId] = useState(searchParams.get('houseId') ?? '');
    const { data: houses = [], isLoading: housesLoading, isError: housesError } = useGetHousesQuery();
    const selectedHouse = houses.find((house) => house.id === houseId);
    /** The houses and locations at an address that already exists — the 409 `address_exists`. */
    const [addressConflict, setAddressConflict] = useState<AddressConflict | null>(null);
    const conflictMode = addressConflict ? addressConflictMode(addressConflict) : null;
    const [conflictHouseName, setConflictHouseName] = useState('');
    const [conflictError, setConflictError] = useState<string | null>(null);
    const [createStation, { isLoading: creatingHouse }] = useCreateStationMutation();

    // Which use cases this tenant may create, derived from its configured
    // services. New onboarded (self-service) room tenants are venue-only;
    // legacy room tenants keep venue + apartment. Fall back to the whole room
    // family (the space template) before the config loads so the dropdown is
    // never empty.
    const config = useSelector((state: RootState) => state.authentication.config);
    const availableUseCases = useMemo<UseCase[]>(() => {
        const base: UseCase[] = liveUseCasesWhere((d) => d.template === 'space');
        const services = config?.services;
        if (!services) return base;
        const filtered = base.filter((uc) => services.some((service) => service === uc));
        return filtered.length > 0 ? filtered : base;
    }, [config]);

    // Fetch the existing asset list so we can warn the user before creating
    // a duplicate. Reuses the availability endpoint that the assets overview
    // already populates; the cache is typically warm by the time the user
    // arrives here from the overview page. If not loaded yet, we fail open
    // and skip the check rather than block the save button.
    // D12: the room products this object can be created into, filtered to the
    // chosen use case (compare case-insensitively, compartment fallback for
    // object-minted products whose `use_case` is null).
    const { data: products = [], isLoading: productsLoading } = useGetProductsQuery();
    // #3167: the product IS the service. Every room product the tenant may
    // create is offered, and the service (use case) follows from the pick.
    const roomProducts = useMemo(
        () => productsForNewRoom(products, availableUseCases),
        [products, availableUseCases],
    );
    const { data: categories = [] } = useTenantProductCategoriesQuery();
    const selectedProduct = roomProducts.find((p) => p.id === form.productId);
    // Shown read-only as the object's service: the product's category, the
    // tenant's own name for it, before the bare use case.
    const serviceName = categories.find((c) => c.id === selectedProduct?.productCategoryId)?.name;

    const now = useMemo(() => new Date(), []);
    const { data: existingAssets } = useGetAssetsAvailabilityDataQuery({
        data: {
            year: String(now.getFullYear()),
            month: String(now.getMonth() + 1),
        },
    });

    const updateForm = useCallback(<K extends keyof CreateFormState>(key: K, value: CreateFormState[K]) => {
        setForm((prev) => ({ ...prev, [key]: value }));
    }, []);

    const handleNameChange = useCallback((val: string) => {
        nameTouchedRef.current = true;
        setForm((prev) => ({ ...prev, name: val }));
    }, []);

    useEffect(() => {
        if (nameTouchedRef.current) return;
        const composed = composeName(form.address, form.designation);
        if (composed && composed !== form.name) {
            setForm((prev) => ({ ...prev, name: composed }));
        }
    }, [form.address, form.designation, form.name]);

    // Picking the product picks the service: the use case is the product's,
    // never asked for separately (#3167).
    const handleProductChange = useCallback((productId: string) => {
        const product = roomProducts.find((p) => p.id === productId);
        setForm((prev) => ({
            ...prev,
            productId,
            useCase: roomUseCaseOfProduct(product, availableUseCases),
        }));
    }, [roomProducts, availableUseCases]);

    // A tenant with exactly one room product gets it pre-selected, so the
    // form is usable at once instead of a one-item dropdown to open.
    useEffect(() => {
        if (form.productId || roomProducts.length !== 1) return;
        handleProductChange(roomProducts[0]!.id);
    }, [form.productId, roomProducts, handleProductChange]);

    const updateAddress = useCallback((updates: Partial<AssetAddress>) => {
        setForm((prev) => ({ ...prev, address: { ...prev.address, ...updates } }));
    }, []);

    /**
     * A house's address becomes the form's, so the name is composed from it
     * exactly as from a typed one. The address itself is not sent: the backend
     * takes the house's location.
     */
    const takeHouseAddress = useCallback((id: string) => {
        const house = houses.find((candidate) => candidate.id === id);
        if (!house?.address) return;
        updateAddress({
            street: house.address.street ?? '',
            houseNumber: house.address.houseNumber ?? '',
            postalCode: house.address.postalCode ?? '',
            city: house.address.city ?? '',
        });
    }, [houses, updateAddress]);

    const handleHouseChange = useCallback((id: string) => {
        setHouseId(id);
        takeHouseAddress(id);
    }, [takeHouseAddress]);

    // A house preselected through the URL: take its address once the list has
    // loaded. Guarded by a ref so it runs once and never races a later pick.
    const houseAddressTaken = useRef(false);
    useEffect(() => {
        if (houseAddressTaken.current || placement !== 'house' || !selectedHouse) return;
        houseAddressTaken.current = true;
        takeHouseAddress(selectedHouse.id);
    }, [placement, selectedHouse, takeHouseAddress]);

    const updateAccommodation = useCallback((updates: Partial<AccommodationDetail>) => {
        setForm((prev) => ({ ...prev, accomodationdetail: { ...prev.accomodationdetail, ...updates } }));
    }, []);

    const updateAmenities = useCallback((amenities: AccommodationDetailAmenity[]) => {
        updateAccommodation({ amenities });
    }, [updateAccommodation]);

    const updateService = useCallback((serviceId: string, updates: Partial<FormService>) => {
        setForm((prev) => ({
            ...prev,
            additionalServices: prev.additionalServices.map((s) =>
                s.id === serviceId ? { ...s, ...updates } : s,
            ),
        }));
    }, []);

    const defaultTaxCategoryId = useDefaultTaxCategoryId();
    const addService = useCallback(() => {
        setForm((prev) => ({
            ...prev,
            additionalServices: [
                ...prev.additionalServices,
                {
                    id: `new-${Date.now()}`,
                    serviceName: '',
                    langNames: { de: '', en: '' },
                    unitPrice: 0,
                    isPaymentCompulsory: false,
                    quantity: false,
                    // #1353/#2959: a new service starts on the tenant's default
                    // category, shown in its picker so the operator sees and can
                    // change it. Pricing never falls back to it: '' (no default, or
                    // the catalogue not loaded yet) still withholds the breakdown and
                    // says so, which is the prompt to choose.
                    taxCategoryId: defaultTaxCategoryId,
                    priceUnit: null,
                    isNew: true,
                },
            ],
        }));
    }, [defaultTaxCategoryId]);

    const removeService = useCallback((serviceId: string) => {
        setForm((prev) => ({
            ...prev,
            additionalServices: prev.additionalServices.filter((s) => s.id !== serviceId),
        }));
    }, []);

    const updatePolicies = useCallback((policies: AccommodationDetailPolicy[]) => {
        updateForm('policies', policies);
    }, [updateForm]);

    const updateOpeningHours = useCallback((hours: OpeningHourEntry[]) => {
        updateForm('openingHours', hours);
    }, [updateForm]);

    // Hourly rooms only (venues), and the code is right where the comment above it used to be
    // wrong: it claimed apartments too, which would have written seven
    // `openinghours` rows that nothing ever reads. An apartment's availability
    // comes from `findAvailableApartments` (raw SQL, `groupService.ts`), which
    // never touches the table; only the station use cases price and offer slots
    // through `availabilityService`, where a weekday with no row means CLOSED.
    // So asking for a venue's hours is meaningful and asking for an apartment's
    // is not — the field would be a promise the booking flow does not keep.
    // In a house the hours are the house's, shown read-only (A1 a).
    const showsOpeningHours = spaceOf(form.useCase)?.timeUnit === 'hour' && placement === 'new';
    const inHouse = placement === 'house';

    // ─── Validation ────────────────────────────────────────────────────────
    const validationError = useMemo((): string | null => {
        // D12: the object is created INTO a product — prices come from there,
        // and so does its service (#3167). Without one the object would be
        // unpriced and invisible to customers (ADR-0018).
        if (!form.productId || !form.useCase) return t('asset_create.validation.product_required');
        if (!form.featuredImage) return t('asset_create.validation.featured_image_required');
        if (!form.name.trim()) return t('asset_create.validation.name_required');
        if (inHouse && !houseId) return t('asset_create.validation.house_required');
        const addressError = inHouse ? null : newAddressError(form.address, t);
        if (addressError) return addressError;
        // Capacity is load-bearing, not cosmetic: the availability query filters
        // assets by `accomodationdetail.guests >= <requested guests>`. An asset
        // saved without it is invisible in every booking dropdown, so block the
        // save here rather than let the tenant create an unbookable object.
        const { guests } = form.accomodationdetail;
        if (guests == null || guests < 1) {
            return t('asset_create.validation.guests_required');
        }
        if (showsOpeningHours && !openingHoursValid) {
            return t('asset_create.validation.opening_hours_invalid');
        }
        return null;
    }, [form, openingHoursValid, showsOpeningHours, inHouse, houseId, t]);

    // ─── Save flow ─────────────────────────────────────────────────────────
    /**
     * `confirmNewLocation`: a second location at an existing address, asked.
     * `intoHouseId`: the house the address dialog just created (6.2) — the room
     * goes in there rather than to the form's address.
     */
    const openAddressConflict = useCallback((conflict: AddressConflict) => {
        const mode = addressConflictMode(conflict);
        setConflictHouseName(mode.mode === 'create_house' ? (mode.location.name ?? '') : '');
        setConflictError(null);
        setAddressConflict(conflict);
    }, []);

    const handleSave = useCallback(async (confirmNewLocation = false, intoHouseId?: string) => {
        if (validationError) {
            setError(validationError);
            return;
        }
        if (!form.useCase) return; // guarded by validationError, keeps TS happy

        // Soft duplicate check: warn the user before creating an asset whose
        // name matches an existing one. The backend does not enforce
        // uniqueness today, so the only defense here is frontend.
        const trimmedName = form.name.trim().toLowerCase();
        const duplicate = existingAssets?.find(
            (a) => a.assetName.trim().toLowerCase() === trimmedName,
        );
        if (duplicate && !confirmNewLocation && !intoHouseId) {
            // eslint-disable-next-line no-alert
            const proceed = window.confirm(t('asset_create.duplicate_confirm'));
            if (!proceed) return;
        }

        setError(null);
        const targetHouseId = intoHouseId ?? (inHouse ? houseId : null);
        try {
            const created = await createAssetWithAddress({
                name: form.name.trim(),
                floor: form.address.floor.trim() || null,
                useCase: form.useCase,
                ...placementFieldsOf(form, targetHouseId, confirmNewLocation),
                accomodationdetail: {
                    ...form.accomodationdetail,
                    policies: form.policies,
                },
                assetAdditionalServices: form.additionalServices.map((s) => ({
                    serviceName: s.langNames?.de || s.serviceName,
                    langNames: s.langNames,
                    unitPrice: s.unitPrice,
                    isPaymentCompulsory: s.isPaymentCompulsory,
                    quantity: s.quantity,
                    priceUnit: s.priceUnit,
                })),
                productId: form.productId,
                // A room in a house brings no hours of its own: they are the house's.
                openingHours: targetHouseId ? undefined : ownOpeningHoursOf(form, showsOpeningHours),
                featuredImage: form.featuredImage,
                additionalImages: form.additionalImages.length > 0 ? form.additionalImages : undefined,
            }).unwrap();

            // Every inventory reader, not a hand-picked few: availability, the
            // house picker's room counts and a house page's room list all
            // include this room now.
            invalidateInventoryReaders(dispatch);
            navigate(makeAssetDetailsUrl(created.id));
        } catch (err: unknown) {
            const e = err as { data?: { message?: string }, message?: string };
            // An identical address already exists: ask, rather than show an
            // error — the operator may well mean that house.
            const conflict = addressConflictOf(err);
            if (conflict) {
                openAddressConflict(conflict);
                return;
            }
            setError(objectLimitMessage(e?.data, t) ?? e?.data?.message ?? e?.message ?? t('asset_create.save_error'));
        }
    }, [
        form, validationError, showsOpeningHours, createAssetWithAddress, navigate, t, dispatch,
        existingAssets, inHouse, houseId, openAddressConflict,
    ]);

    /** "Put it in that house instead" from the address dialog. */
    const placeInConflictHouse = useCallback((id: string) => {
        setAddressConflict(null);
        setPlacement('house');
        handleHouseChange(id);
    }, [handleHouseChange]);

    /**
     * 6.2: a house at the existing location, and the room straight into it.
     * Two writes, house first: if the room then fails, the house stands and
     * the form switches to it, so a retry does not create a second house.
     */
    const createHouseForConflict = useCallback(async () => {
        if (conflictMode?.mode !== 'create_house' || !form.useCase || creatingHouse) return;
        setConflictError(null);
        try {
            const house = await createStation({
                name: conflictHouseName.trim(),
                locationId: conflictMode.location.id,
                ...(showsOpeningHours && form.openingHours.length > 0
                    ? { openingHours: form.openingHours }
                    : {}),
                doorCount: 0,
                bufferDoorsCount: 0,
                containerType: 'house',
            }).unwrap();
            setAddressConflict(null);
            setPlacement('house');
            handleHouseChange(house.id);
            await handleSave(false, house.id);
        } catch (err: unknown) {
            const e = err as { data?: { message?: string } };
            setConflictError(e?.data?.message ?? t('asset_create.address_exists.create_house_error'));
        }
    }, [
        conflictMode, form.useCase, form.openingHours, creatingHouse, createStation, conflictHouseName,
        showsOpeningHours, handleHouseChange, handleSave, t,
    ]);

    return (
        <div className={'p-6 flex flex-col gap-6'}>
            <div className={'sticky top-16 z-30 bg-background -mx-6 px-6 -mt-6 pt-6 pb-3'}>
                <PageHeader>
                    <PageHeadline>
                        {t('asset_create.title')}
                        {form.name.trim() && (
                            <span className={'block text-sm font-normal text-foreground/60 mt-0.5'}>
                                {form.name.trim()}
                            </span>
                        )}
                    </PageHeadline>
                </PageHeader>
            </div>

            {error && (
                <div className={'bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-800'}>
                    {error}
                </div>
            )}

            {/* Product — and with it the service (D12, #3167): prices and
                discounts live on the product, and the product is the
                service, so it comes first and nothing asks for a service. */}
            <AssetDetailSection title={t('asset_create.product.title')}>
                <p className={'text-sm text-foreground/60 mb-3'}>
                    {t('asset_create.product.hint')}
                </p>
                <SelectField
                    label={`${t('asset_create.product.label')} *`}
                    value={form.productId}
                    onChange={handleProductChange}
                    options={roomProducts.map((p) => p.id)}
                    optionLabels={Object.fromEntries(
                        roomProducts.map((p) => [p.id, p.name ?? p.id]),
                    )}
                    placeholder={t('asset_create.product.placeholder')}
                    noOptions={t('asset_create.product.none_for_use_case')}
                    loading={productsLoading}
                    className={'max-w-md'}
                />
                {!productsLoading && roomProducts.length === 0 && (
                    <p className={'text-sm text-foreground/60 mt-3'}>
                        {t('asset_create.product.empty_hint')}{' '}
                        <button
                            type={'button'}
                            className={'text-primary hover:text-primary/80 font-medium underline-offset-2 underline'}
                            onClick={() => navigate(paths.rentalProducts)}
                        >
                            {t('asset_create.product.go_to_services')}
                        </button>
                    </p>
                )}
            </AssetDetailSection>

            {/* Basic info */}
            <BasicInfoSection
                mode={'create'}
                formName={form.name}
                onNameChange={handleNameChange}
                designation={form.designation}
                onDesignationChange={(val) => updateForm('designation', val)}
                compartmentTypes={form.useCase ? [form.useCase] : []}
                serviceName={serviceName}
            />

            {/* Where the room stands: a new address (address + hours, as
                before) or an existing house (its address and hours). */}
            <RoomPlacementSection
                placement={placement}
                onPlacementChange={setPlacement}
                houses={houses}
                housesLoading={housesLoading}
                housesError={housesError}
                useCase={form.useCase}
                houseId={houseId}
                onHouseChange={handleHouseChange}
                floor={form.address.floor}
                onFloorChange={(floor) => updateAddress({ floor })}
            >
                <AddressSection
                    address={form.address}
                    onChange={updateAddress}
                />

                {/* Opening Hours (venues only) */}
                {showsOpeningHours && (
                    <OpeningHoursSection
                        openingHours={form.openingHours}
                        onChange={updateOpeningHours}
                        onValidityChange={setOpeningHoursValid}
                        emptyMeansAlwaysOpen
                    />
                )}
            </RoomPlacementSection>

            {/* Images */}
            <CreateImagesSection
                featuredImage={form.featuredImage}
                additionalImages={form.additionalImages}
                onFeaturedChange={(file) => updateForm('featuredImage', file)}
                onAdditionalChange={(files) => updateForm('additionalImages', files)}
            />

            {/* Accommodation */}
            {form.useCase ? (
                <AccommodationSection
                    amenities={form.accomodationdetail.amenities}
                    useCase={form.useCase}
                    mode={'create'}
                    formData={form.accomodationdetail}
                    onAccommodationChange={updateAccommodation}
                    onAmenitiesChange={updateAmenities}
                />
            ) : (
                <div className={'text-sm text-foreground/80 bg-foreground/5 border border-foreground/10 rounded-lg px-4 py-3'}>
                    {t('asset_create.service_required_placeholder')}
                </div>
            )}

            {/* Policies */}
            <PoliciesSection
                policies={form.policies}
                mode={'create'}
                onPoliciesChange={updatePolicies}
            />

            {/* Additional Services */}
            <AdditionalServicesSection
                services={[]}
                mode={'create'}
                formServices={form.additionalServices}
                onServiceChange={updateService}
                onAddService={addService}
                onRemoveService={removeService}
            />

            {/* Sticky save bar */}
            <div className={'sticky bottom-0 bg-text-contrast border-t border-foreground/10 px-6 py-3 flex justify-end gap-3 shadow-lg z-10 -mx-6 -mb-6'}>
                <CustomButton
                    title={t('common.cancel')}
                    onClick={() => navigate(paths.assets)}
                    isFilled={false}
                    disabled={saving}
                />
                <Tooltip title={validationError ?? ''} disableHoverListener={!validationError} arrow>
                    <span>
                        <CustomButton
                            title={t('asset_create.save')}
                            onClick={() => { handleSave(); }}
                            isFilled={true}
                            disabled={saving || validationError !== null}
                            loading={saving}
                        />
                    </span>
                </Tooltip>
            </div>

            {/* An identical address already exists (409 `address_exists`). */}
            <CustomModal
                open={addressConflict !== null}
                onClose={() => setAddressConflict(null)}
                title={t('asset_create.address_exists.title')}
                description={(() => {
                    if (conflictMode?.mode === 'houses') {
                        return t('asset_create.address_exists.with_houses', {
                            count: conflictMode.houses.length,
                            houses: conflictMode.houses.map((house) => `„${house.name}“`).join(', '),
                        });
                    }
                    if (conflictMode?.mode === 'create_house') {
                        return t(conflictMode.location.hasRooms
                            ? 'asset_create.address_exists.without_house_with_room'
                            : 'asset_create.address_exists.without_house', {
                            location: conflictMode.location.name ?? '',
                        });
                    }
                    return t('asset_create.address_exists.location_exists');
                })()}
                saveButton={
                    <CustomButton
                        title={t('asset_create.address_exists.create_anyway')}
                        onClick={() => {
                            setAddressConflict(null);
                            handleSave(true);
                        }}
                        isFilled={false}
                    />
                }
                closeButton={
                    <CustomButton
                        title={t('common.cancel')}
                        isFilled={false}
                        onClick={() => setAddressConflict(null)}
                    />
                }
            >
                <div className={'flex flex-col items-start gap-3'}>
                    {conflictMode?.mode === 'houses' && conflictMode.houses.map((house) => (
                        <CustomButton
                            key={house.id}
                            title={t('asset_create.address_exists.choose_house', { house: house.name })}
                            onClick={() => placeInConflictHouse(house.id)}
                        />
                    ))}
                    {conflictMode?.mode === 'create_house' && (
                        <>
                            <InputField
                                label={t('asset_create.address_exists.house_name_label')}
                                value={conflictHouseName}
                                onChange={setConflictHouseName}
                            />
                            <CustomButton
                                title={t('asset_create.address_exists.create_house')}
                                onClick={createHouseForConflict}
                                disabled={conflictHouseName.trim().length === 0 || creatingHouse || saving}
                                loading={creatingHouse || saving}
                            />
                        </>
                    )}
                    {conflictError && <p className={'text-sm text-error'}>{conflictError}</p>}
                    <p className={'text-xs text-foreground/60'}>
                        {t('asset_create.address_exists.create_anyway_hint')}
                    </p>
                </div>
            </CustomModal>
        </div>
    );
};

export default AssetCreatePage;
